import { useState, useLayoutEffect, useRef } from 'react';
import className from 'classnames';

export type Tab<T> = {
  label: string;
  fullLabel?: string;
  value?: T;
  disabled?: boolean;
  activeClasses?: {
    btn?: string;
    pill?: string;
  };
};

export const TabSelect = <T extends Tab<U>, U>({
  tabs,
  selectedTab,
  onSelectionChange,
  btnClasses,
  pillHeight = 34,
  pillWidth,
  containerMargin = 5,
}: {
  tabs: readonly T[];
  selectedTab: T;
  onSelectionChange: (option: T) => void;
  btnClasses?: string;
  pillHeight?: number;
  pillWidth?: number; // override content width
  containerMargin?: number;
}) => {
  const [pillDimensions, setPillDimensions] = useState<{ offsetLeft: number; width: number }>();
  const selectedButtonRef = useRef<HTMLButtonElement | null>(null);

  useLayoutEffect(() => {
    if (selectedButtonRef.current) {
      const element = selectedButtonRef.current;
      setPillDimensions({
        offsetLeft: element.offsetLeft,
        width: element.offsetWidth,
      });
    }
  }, [selectedTab.label]);

  return (
    <div
      className="relative z-0 flex h-fit rounded-md border border-cf-gray-4 bg-cf-gray-3-5"
      style={{ padding: `${containerMargin}px` }}
    >
      {/* only render selection pill once we know the offset to prevent animating initial position */}
      {pillDimensions && (
        <div
          className={className(
            'pointer-events-none absolute z-0 border border-cf-gray-5 bg-cf-gray-4 duration-150 ease-in-out',
            selectedTab.activeClasses?.pill,
          )}
          style={{
            width: pillWidth ? `${pillWidth}px` : `${pillDimensions.width}px`,
            height: `${pillHeight}px`,
            // offset within parent - padding of parent
            transform: `translateX(${pillDimensions.offsetLeft - containerMargin}px)`,
            // keeps the distance between the outer and inner borders consistent
            borderRadius: `${6 - containerMargin}px`,
          }}
        />
      )}
      {tabs.map((tab) => {
        const isSelected = tab.label === selectedTab.label;
        const activeBtnClasses = selectedTab.activeClasses?.btn;
        return (
          <button
            key={tab.label}
            type="button"
            disabled={tab.disabled}
            className={className(
              'z-10 rounded-[4px] px-2 text-center font-aeonikMedium transition ease-in',
              isSelected && !activeBtnClasses && 'text-white',
              isSelected && activeBtnClasses,
              tab.disabled ? 'text-cf-gray-5' : !isSelected && 'text-cf-light-2 hover:text-white',
              btnClasses,
            )}
            onClick={!isSelected ? () => onSelectionChange(tab) : undefined}
            style={{
              height: `${pillHeight}px`,
              width: pillWidth ? `${pillWidth}px` : 'auto',
            }}
            ref={isSelected ? selectedButtonRef : undefined}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};
